import { useMemo } from 'react';
import { getNumberOfSelectedFilters } from '../lib/FilterLib';
import {
  DefaultFilters,
  FilterPreferenceRecognizedLists,
} from '../services/modules/userContextPreferences/userContextPreferences.types';
import { ArrayUtil } from '../utils/array.utils';
import useSearchParams from './useSearchParams';
import { useUserContextPreferences } from './useUserContextPreferences';

interface UseDefaultFiltersToListProps {
  list: FilterPreferenceRecognizedLists;
  filtersKeys: string[];
  filterKeysPrefix?: string[];
}

const useDefaultFiltersToList = ({
  list,
  filtersKeys,
  filterKeysPrefix,
}: UseDefaultFiltersToListProps) => {
  const { searchParams } = useSearchParams();

  const { getDefaultFiltersToList } = useUserContextPreferences();
  const defaultFiltersToList = getDefaultFiltersToList(list);

  const handleGetFilterKeys = () => {
    if (!filterKeysPrefix) {
      return filtersKeys;
    }

    const valuesKey = Object.keys(searchParams);
    const keysWithPrefix = valuesKey.filter((key) =>
      filterKeysPrefix.some((prefix) => key.startsWith(prefix)),
    );

    return [...keysWithPrefix, ...filtersKeys];
  };

  const verifyIfShouldIgnoreDefaultFilters = () => {
    const allFilterKeys = handleGetFilterKeys();

    const totalAppliedFilters = getNumberOfSelectedFilters(searchParams, allFilterKeys);

    if (totalAppliedFilters > 0 || !defaultFiltersToList) {
      return true;
    }

    return false;
  };

  const defaultFiltersByPrefix = useMemo(() => {
    if (!defaultFiltersToList || !filterKeysPrefix?.length) {
      return {};
    }

    const filters: DefaultFilters = {};

    for (const prefix of filterKeysPrefix) {
      const prefixFilterValues = Object.keys(defaultFiltersToList)
        .filter((key) => key.startsWith(prefix))
        .flatMap((key) => ArrayUtil.getAsArray(defaultFiltersToList[key]));

      filters[prefix] = prefixFilterValues;
    }

    return filters;
  }, [defaultFiltersToList, filterKeysPrefix]);

  return {
    defaultFilters: defaultFiltersToList,
    defaultFiltersByPrefix,
    shouldIgnoreDefaultFilters: verifyIfShouldIgnoreDefaultFilters(),
  };
};

export default useDefaultFiltersToList;
